import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { withAsyncPaginate } from 'react-select-async-paginate';
import invoiceService from '../../services/InvoiceService';
import DueDateInput from './DueDateInput';
import {
  boxshallProductOption,
  dueDateFromVendorTerm,
  emptyLineItem,
  isLineAmountValid,
  validateInvoiceTotals
} from './helper';

const AsyncPaginateCreatable = withAsyncPaginate(CreatableSelect);
const MIN_IMAGE_ZOOM = 25;
const MAX_IMAGE_ZOOM = 300;
const IMAGE_ZOOM_STEP = 25;

export const ocrWordsFromBlocks = (blocks = []) =>
  blocks.flatMap((block) =>
    (block.paragraphs || []).flatMap((paragraph) =>
      (paragraph.lines || []).flatMap((line) =>
        (line.words || [])
          .filter((word) => word.text?.trim() && word.bbox)
          .map((word, wordIndex, words) => ({
            text: word.text.trim(),
            bbox: word.bbox,
            suffix: wordIndex === words.length - 1 ? '\n' : ' '
          }))
      )
    )
  );

const InvoiceDialogContent = ({
  open,
  row,
  customerRefs,
  classRefs,
  taxCodes,
  defaultTaxCode,
  vendorList,
  vendorLoading,
  qboTerms,
  qboTermsLoading,
  boxshallProducts,
  boxshallProductsLoading,
  boxshallProductsError,
  loadBoxshallProductOptions,
  referencesLoading,
  taxCodeLoading,
  onCreateBoxshallProduct,
  onCheckDocNumbers,
  onCancelChanges,
  onApplyChanges
}) => {
  const [draft, setDraft] = useState(row || {});
  const [saving, setSaving] = useState(false);
  const [extractedFile, setExtractedFile] = useState({ url: '', type: '', loading: false, error: '' });
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceFileUrl, setInvoiceFileUrl] = useState('');
  const [invoiceFileError, setInvoiceFileError] = useState('');
  const [draggingInvoiceFile, setDraggingInvoiceFile] = useState(false);
  const [imageZoom, setImageZoom] = useState(100);
  const [loadedImage, setLoadedImage] = useState({ url: '', width: 0, height: 0 });
  const [ocrWords, setOcrWords] = useState([]);
  const [ocrStatus, setOcrStatus] = useState('idle');
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrError, setOcrError] = useState('');
  const [ocrAttempt, setOcrAttempt] = useState(0);
  const [docNumberCheck, setDocNumberCheck] = useState(null);
  const [checkingDocNumber, setCheckingDocNumber] = useState(false);
  const docNumberRequestRef = useRef(0);
  const invoiceFileInputRef = useRef(null);
  const customerOptions = customerRefs.map((reference) => ({ value: reference.value, label: reference.name }));
  const classOptions = classRefs.map((reference) => ({ value: reference.value, label: reference.name }));
  const taxCodeOptions = taxCodes.map((reference) => ({ value: reference.value, label: reference.name }));
  const vendorOptions = vendorList.map((vendor) => ({
    value: vendor.id || vendor.value,
    label: vendor.label || vendor.value,
    TermRef: vendor.TermRef || null
  }));
  const boxshallProductOptions = boxshallProducts
    .map(boxshallProductOption)
    .filter((option) => option.value && option.label);
  const invoiceTotalValidation = validateInvoiceTotals(draft);
  const billableStatus =
    Boolean(draft.lineItems?.length) && draft.lineItems.every((lineItem) => lineItem.billableStatus);
  const selectedBoxshallProductOption = (item) => {
    const localId = item.boxshallProduct?.id;
    const qboId = item.boxshallProduct?.value;
    const selected = boxshallProductOptions.find(
      (option) =>
        (localId && String(option.localId) === String(localId)) || (qboId && String(option.qboId) === String(qboId))
    );
    if (selected) return selected;
    if (!localId && !qboId) return null;
    return {
      value: String(localId || qboId),
      localId: localId ? String(localId) : null,
      qboId: qboId ? String(qboId) : '',
      name: item.boxshallName,
      label: item.boxshallName,
      description: item.boxshallDescription || ''
    };
  };

  useEffect(() => {
    setDraft(row || {});
    setInvoiceFile(null);
    setInvoiceFileError('');
    setDocNumberCheck(
      row?.docNumberDuplicate
        ? {
            docNumber: row.invoiceNumber,
            duplicate: true,
            message: row.docNumberDuplicateMessage
          }
        : null
    );
  }, [row]);

  useEffect(() => {
    if (!invoiceFile) {
      setInvoiceFileUrl('');
      return undefined;
    }
    // eslint-disable-next-line no-undef
    if (typeof URL.createObjectURL !== 'function') return undefined;
    // eslint-disable-next-line no-undef
    const objectUrl = URL.createObjectURL(invoiceFile);
    setInvoiceFileUrl(objectUrl);
    return () => {
      // eslint-disable-next-line no-undef
      URL.revokeObjectURL(objectUrl);
    };
  }, [invoiceFile]);

  useEffect(() => {
    if (!open || !row) return undefined;

    const directUrl = row.source?.url || row.source?.fileUrl || row.fileUrl;
    if (directUrl) {
      setExtractedFile({
        url: directUrl,
        type: row.source?.contentType || row.contentType || '',
        loading: false,
        error: ''
      });
      return undefined;
    }

    const invoiceId = row.invoiceId || row._id;
    if (!invoiceId) {
      setExtractedFile({ url: '', type: '', loading: false, error: 'No invoice file is available.' });
      return undefined;
    }

    let active = true;
    let objectUrl = '';
    setExtractedFile({ url: '', type: '', loading: true, error: '' });
    invoiceService
      .downloadInvoicePdf(invoiceId)
      .then((response) => {
        if (!active) return;
        // eslint-disable-next-line no-undef
        objectUrl = URL.createObjectURL(response.data);
        setExtractedFile({
          url: objectUrl,
          type: response.data?.type || response.headers?.['content-type'] || '',
          loading: false,
          error: ''
        });
      })
      .catch(() => {
        if (active) setExtractedFile({ url: '', type: '', loading: false, error: 'Could not load the invoice file.' });
      });

    return () => {
      active = false;
      // eslint-disable-next-line no-undef
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, row]);

  const displayedFile = invoiceFile
    ? { url: invoiceFileUrl, type: invoiceFile.type, loading: false, error: '' }
    : extractedFile;
  const displayedFileIsImage =
    displayedFile.type.startsWith('image/') ||
    /\.(?:avif|gif|heic|heif|jpe?g|png|svg|webp)(?:[?#].*)?$/i.test(displayedFile.url);
  const isManualVerdInvoice = row?.source?.mailbox === 'manual-entry@verd-space';

  useEffect(() => {
    setImageZoom(100);
    setLoadedImage({ url: '', width: 0, height: 0 });
    setOcrWords([]);
    setOcrStatus('idle');
    setOcrProgress(0);
    setOcrError('');
  }, [displayedFile.url, open]);

  useEffect(() => {
    if (!open || !displayedFileIsImage || !displayedFile.url || loadedImage.url !== displayedFile.url) {
      return undefined;
    }

    let active = true;
    let worker;
    setOcrWords([]);
    setOcrStatus('loading');
    setOcrProgress(0);
    setOcrError('');

    const recognizeImage = async () => {
      try {
        const { createWorker } = await import('tesseract.js');
        worker = await createWorker('eng', 1, {
          logger: (message) => {
            if (!active || !Number.isFinite(message.progress)) return;
            setOcrProgress(Math.round(message.progress * 100));
          }
        });
        if (!active) {
          await worker.terminate();
          worker = null;
          return;
        }

        const result = await worker.recognize(displayedFile.url, {}, { text: true, blocks: true });
        if (!active) return;
        const words = ocrWordsFromBlocks(result.data.blocks || []);
        setOcrWords(words);
        setOcrStatus('ready');
        if (!words.length) setOcrError('No selectable text was detected in this image.');
      } catch (_error) {
        if (!active) return;
        setOcrStatus('error');
        setOcrError('Image text could not be recognised.');
      } finally {
        if (worker) {
          await worker.terminate().catch(() => undefined);
          worker = null;
        }
      }
    };

    recognizeImage();
    return () => {
      active = false;
      if (worker) worker.terminate().catch(() => undefined);
    };
  }, [displayedFile.url, displayedFileIsImage, loadedImage.url, ocrAttempt, open]);

  const chooseInvoiceFile = (file) => {
    if (!file) return;
    const fileName = String(file.name || '').toLowerCase();
    const supported = file.type === 'application/pdf' || file.type.startsWith('image/') || fileName.endsWith('.pdf');
    if (!supported) {
      setInvoiceFile(null);
      setInvoiceFileError('Only PDF and image invoice files are supported.');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setInvoiceFile(null);
      setInvoiceFileError('The invoice file must be 20 MB or smaller.');
      return;
    }
    setInvoiceFile(file);
    setInvoiceFileError('');
  };

  const updateField = (name, value) => setDraft((current) => ({ ...current, [name]: value }));
  const updateBillBillableStatus = (value) =>
    setDraft((current) => ({
      ...current,
      lineItems: (current.lineItems || []).map((lineItem) => ({ ...lineItem, billableStatus: value }))
    }));
  const updateBillNumber = (value) => {
    docNumberRequestRef.current += 1;
    setCheckingDocNumber(false);
    updateField('invoiceNumber', value);
    if (
      String(docNumberCheck?.docNumber || '')
        .trim()
        .toLowerCase() !==
      String(value || '')
        .trim()
        .toLowerCase()
    ) {
      setDocNumberCheck(null);
    }
  };

  const checkBillNumber = async () => {
    const invoiceId = draft.invoiceId || draft._id;
    const docNumber = String(draft.invoiceNumber || '').trim();
    const requestId = docNumberRequestRef.current + 1;
    docNumberRequestRef.current = requestId;
    if (!invoiceId || !docNumber || !draft.supplierValue) {
      setDocNumberCheck(null);
      return;
    }

    setCheckingDocNumber(true);
    try {
      const [result] = await onCheckDocNumbers([{ invoiceId, docNumber, supplierValue: draft.supplierValue }]);
      if (docNumberRequestRef.current === requestId) setDocNumberCheck(result || null);
    } catch (_error) {
      if (docNumberRequestRef.current === requestId) setDocNumberCheck(null);
    } finally {
      if (docNumberRequestRef.current === requestId) setCheckingDocNumber(false);
    }
  };
  const updateLineItem = (index, name, value) => {
    setDraft((current) => ({
      ...current,
      lineItems: current.lineItems.map((item, itemIndex) => {
        if (name === 'customerRef' || name === 'classRef') return { ...item, [name]: value };
        if (itemIndex !== index) return item;
        if (name.endsWith('Amount')) {
          const moneyField = name.replace('Amount', '');
          return {
            ...item,
            [moneyField]: {
              ...(item[moneyField] || {}),
              amount: value,
              currency: current.currency || 'AUD'
            }
          };
        }
        return { ...item, [name]: value };
      })
    }));
  };

  const applyBoxshallProductToLine = (index, product) => {
    setDraft((current) => ({
      ...current,
      lineItems: current.lineItems.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              boxshallName: product?.name || '',
              boxshallProduct: product
                ? {
                    id: product.id,
                    value: product.value,
                    name: product.name
                  }
                : null,
              ...(product && { boxshallDescription: product.description || '' })
            }
          : item
      )
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onApplyChanges(draft, invoiceFile);
    } finally {
      setSaving(false);
    }
  };

  const changeImageZoom = (change) => {
    setImageZoom((current) => Math.min(MAX_IMAGE_ZOOM, Math.max(MIN_IMAGE_ZOOM, current + change)));
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onCancelChanges} fullWidth maxWidth="xl">
      <DialogTitle>Edit supplier bill</DialogTitle>
      <DialogContent dividers>
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-items-start tw-gap-3 tw-pt-2">
          <div className="tw-flex tw-flex-col tw-gap-1">
            <Typography variant="caption" color="text.secondary">
              Supplier
            </Typography>
            <ReactSelect
              isClearable
              isSearchable
              isLoading={vendorLoading}
              options={vendorOptions}
              value={vendorOptions.find((option) => String(option.value) === String(draft.supplierValue || '')) || null}
              placeholder={draft.supplierName || 'Select QBO supplier'}
              // eslint-disable-next-line no-undef
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              onChange={(option) => {
                docNumberRequestRef.current += 1;
                setCheckingDocNumber(false);
                setDocNumberCheck(null);
                setDraft((current) => {
                  const vendorDueDate = dueDateFromVendorTerm(current.invoiceDate, '', option?.TermRef);
                  return {
                    ...current,
                    supplierValue: option?.value || null,
                    supplierQboName: option?.label || null,
                    dueDate: vendorDueDate || current.dueDate,
                    ...(vendorDueDate && { dueDateSource: 'vendor' })
                  };
                });
              }}
            />
            {draft.supplierName && (
              <Typography variant="caption" color="text.secondary">
                Extracted as: {draft.supplierName}
              </Typography>
            )}
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <Typography variant="caption">Supplier tax ID</Typography>
            <TextField
              value={draft.supplierTaxId || ''}
              onChange={(event) => updateField('supplierTaxId', event.target.value)}
              size="small"
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <Typography variant="caption">Bill Number</Typography>
            <TextField
              value={draft.invoiceNumber || ''}
              onChange={(event) => updateBillNumber(event.target.value)}
              onBlur={checkBillNumber}
              error={Boolean(docNumberCheck?.duplicate)}
              helperText={
                checkingDocNumber
                  ? 'Checking QuickBooks…'
                  : docNumberCheck?.duplicate
                  ? docNumberCheck.message || 'Bill Number already exists in QuickBooks'
                  : ' '
              }
              size="small"
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <Typography variant="caption">Bill Date</Typography>
            <TextField
              type="date"
              value={draft.invoiceDate || ''}
              onChange={(event) => updateField('invoiceDate', event.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <Typography variant="caption" color="text.secondary">
              Due Date
            </Typography>
            <DueDateInput
              invoiceDate={draft.invoiceDate || ''}
              value={draft.dueDate || ''}
              terms={qboTerms}
              loading={qboTermsLoading}
              onChange={(value) => setDraft((current) => ({ ...current, dueDate: value, dueDateSource: 'manual' }))}
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <Typography variant="caption">Customer</Typography>
            <ReactSelect
              isClearable
              isSearchable
              isLoading={referencesLoading}
              options={customerOptions}
              value={
                customerOptions.find((option) => String(option.value) === String(draft.customerRef?.value || '')) ||
                null
              }
              // eslint-disable-next-line no-undef
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              onChange={(option) => {
                const customerRef = option ? { value: option.value, name: option.label } : null;
                setDraft((current) => ({
                  ...current,
                  customerRef,
                  lineItems: (current.lineItems || []).map((item) => ({ ...item, customerRef }))
                }));
              }}
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <Typography variant="caption">Purchase order</Typography>
            <TextField
              value={draft.purchaseOrder || ''}
              onChange={(event) => updateField('purchaseOrder', event.target.value)}
              size="small"
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <Typography variant="caption">Currency</Typography>
            <TextField
              value={draft.currency || ''}
              onChange={(event) => updateField('currency', event.target.value.toUpperCase())}
              size="small"
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <Typography variant="caption">Subtotal</Typography>
            <TextField
              type="number"
              value={draft.subtotalAmount ?? ''}
              onChange={(event) => updateField('subtotalAmount', event.target.value)}
              error={!invoiceTotalValidation.valid}
              title={!invoiceTotalValidation.valid ? invoiceTotalValidation.message : undefined}
              size="small"
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <Typography variant="caption">Tax</Typography>
            <TextField
              type="number"
              value={draft.totalTaxAmount ?? ''}
              onChange={(event) => updateField('totalTaxAmount', event.target.value)}
              error={!invoiceTotalValidation.valid}
              title={!invoiceTotalValidation.valid ? invoiceTotalValidation.message : undefined}
              size="small"
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <Typography variant="caption">Total</Typography>
            <TextField
              type="number"
              value={draft.totalAmount ?? ''}
              onChange={(event) => updateField('totalAmount', event.target.value)}
              error={!invoiceTotalValidation.valid}
              title={!invoiceTotalValidation.valid ? invoiceTotalValidation.message : undefined}
              size="small"
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <Typography variant="caption">Amount due</Typography>
            <TextField
              type="number"
              value={draft.amountDueAmount ?? ''}
              onChange={(event) => updateField('amountDueAmount', event.target.value)}
              size="small"
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <Typography variant="caption">Supplier address</Typography>
            <TextField
              value={draft.supplierAddress || ''}
              onChange={(event) => updateField('supplierAddress', event.target.value)}
              multiline
              minRows={2}
              className="md:tw-col-span-2 lg:tw-col-span-3"
              size="small"
            />
          </div>

          <div className="tw-grid">
            <Typography variant="caption">Billable Status</Typography>
            <TextField
              select
              size="small"
              value={billableStatus ? 'true' : 'false'}
              onChange={(event) => updateBillBillableStatus(event.target.value === 'true')}
            >
              <MenuItem value="true">Billable</MenuItem>
              <MenuItem value="false">Not billable</MenuItem>
            </TextField>
          </div>
        </div>

        <Divider sx={{ my: 3 }} />
        {!invoiceTotalValidation.valid && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {invoiceTotalValidation.message}
          </Alert>
        )}
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <Typography variant="h6">Product services</Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() =>
              setDraft((current) => ({
                ...current,
                lineItems: [...(current.lineItems || []), emptyLineItem(current.currency || 'AUD', defaultTaxCode)]
              }))
            }
          >
            Add line
          </Button>
        </div>
        <div className="tw-space-y-3">
          {(draft.lineItems || []).map((item, index) => (
            <div
              key={`invoice-line-${index}`}
              className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-3 tw-items-start tw-border tw-border-solid tw-border-gray-200 tw-rounded tw-p-3"
            >
              <div className="tw-grid">
                <Typography variant="caption">Product Service</Typography>
                <TextField
                  size="small"
                  value={item.sku || ''}
                  onChange={(event) => updateLineItem(index, 'sku', event.target.value)}
                />
              </div>

              <div className="tw-grid">
                <Typography variant="caption">Product Service Description</Typography>
                <TextField
                  size="small"
                  value={item.description || ''}
                  onChange={(event) => updateLineItem(index, 'description', event.target.value)}
                />
              </div>

              <div>
                <Typography variant="caption">Boxshall Name</Typography>
                <AsyncPaginateCreatable
                  isClearable
                  isSearchable
                  isLoading={boxshallProductsLoading || undefined}
                  loadOptions={loadBoxshallProductOptions}
                  additional={{ skip: 0 }}
                  defaultOptions={boxshallProductOptions}
                  defaultAdditional={{ skip: 50 }}
                  debounceTimeout={400}
                  noOptionsMessage={() => boxshallProductsError || 'No Boxshall Products found'}
                  value={selectedBoxshallProductOption(item)}
                  placeholder={item.boxshallName || 'Select Boxshall Product'}
                  formatCreateLabel={(inputValue) => `Add new product for "${inputValue.trim()}"`}
                  isValidNewOption={(inputValue, _selectedOptions, availableOptions) => {
                    const search = inputValue.trim().toLowerCase();
                    return Boolean(search) && !availableOptions.some((option) => option.label.toLowerCase() === search);
                  }}
                  onCreateOption={async (inputValue) => {
                    const name = inputValue.trim();
                    if (!name) return;
                    const created = await onCreateBoxshallProduct(draft, index, name, item);
                    if (!created) return;
                    applyBoxshallProductToLine(index, {
                      id: created.value,
                      value: created.qboId,
                      name: created.name,
                      description: created.description
                    });
                  }}
                  // eslint-disable-next-line no-undef
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      borderColor: item.boxshallProduct?.value ? base.borderColor : '#d32f2f',
                      boxShadow: item.boxshallProduct?.value ? base.boxShadow : '0 0 0 1px #d32f2f'
                    })
                  }}
                  onChange={(option) => {
                    applyBoxshallProductToLine(
                      index,
                      option
                        ? {
                            id: option.localId,
                            value: option.qboId,
                            name: option.label,
                            description: option.description
                          }
                        : null
                    );
                  }}
                />
              </div>

              <div className="tw-grid">
                <Typography variant="caption">Boxshall Description</Typography>
                <TextField
                  size="small"
                  value={item.boxshallDescription || ''}
                  onChange={(event) => updateLineItem(index, 'boxshallDescription', event.target.value)}
                />
              </div>

              <div className="tw-grid">
                <Typography variant="caption">Quantity</Typography>
                <TextField
                  size="small"
                  type="number"
                  value={item.quantity ?? ''}
                  onChange={(event) => updateLineItem(index, 'quantity', event.target.value)}
                />
              </div>

              <div className="tw-grid">
                <Typography variant="caption">Product Service Unit</Typography>
                <TextField
                  size="small"
                  value={item.unit || ''}
                  onChange={(event) => updateLineItem(index, 'unit', event.target.value)}
                />
              </div>

              <div className="tw-grid">
                <Typography variant="caption">Product Service Rate</Typography>
                <TextField
                  size="small"
                  type="number"
                  value={item.unitPrice?.amount ?? ''}
                  onChange={(event) => updateLineItem(index, 'unitPriceAmount', event.target.value)}
                  inputProps={{ step: '0.0001' }}
                  error={
                    item.unitPrice?.amount === '' ||
                    item.unitPrice?.amount === null ||
                    item.unitPrice?.amount === undefined ||
                    !isLineAmountValid(item.quantity, item.unitPrice?.amount, item.amount?.amount) ||
                    !invoiceTotalValidation.valid
                  }
                  helperText={
                    item.unitPrice?.amount === '' ||
                    item.unitPrice?.amount === null ||
                    item.unitPrice?.amount === undefined
                      ? 'Required'
                      : !isLineAmountValid(item.quantity, item.unitPrice?.amount, item.amount?.amount)
                      ? 'Check GST inclusion or pack/unit pricing'
                      : ' '
                  }
                />
              </div>

              <div className="tw-grid">
                <Typography variant="caption">Product Service Billable Status</Typography>
                <TextField
                  select
                  size="small"
                  value={item.billableStatus ? 'true' : 'false'}
                  onChange={(event) => updateLineItem(index, 'billableStatus', event.target.value === 'true')}
                >
                  <MenuItem value="true">Billable</MenuItem>
                  <MenuItem value="false">Not billable</MenuItem>
                </TextField>
              </div>

              <div className="tw-grid">
                <Typography variant="caption">Product Service Tax Code</Typography>
                <ReactSelect
                  isClearable
                  isSearchable
                  isLoading={taxCodeLoading}
                  options={taxCodeOptions}
                  value={
                    taxCodeOptions.find((option) => String(option.value) === String(item.taxCode?.value || '')) || null
                  }
                  // eslint-disable-next-line no-undef
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  onChange={(option) =>
                    updateLineItem(index, 'taxCode', option ? { value: option.value, name: option.label } : null)
                  }
                />
              </div>

              <div className="tw-grid">
                <Typography variant="caption">Product Service Markup Percent</Typography>
                <TextField
                  size="small"
                  type="number"
                  value={item.markupPercent ?? ''}
                  onChange={(event) => updateLineItem(index, 'markupPercent', event.target.value)}
                />
              </div>

              <div className="tw-grid">
                <Typography variant="caption">Billable Customer: Product Service</Typography>
                <ReactSelect
                  isClearable
                  isSearchable
                  isLoading={referencesLoading}
                  options={customerOptions}
                  value={customerOptions.find((option) => option.value === item.customerRef?.value) || null}
                  // eslint-disable-next-line no-undef
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  onChange={(option) =>
                    updateLineItem(index, 'customerRef', option ? { value: option.value, name: option.label } : null)
                  }
                />
              </div>

              <div className="tw-grid">
                <Typography variant="caption">Product Service Class</Typography>
                <ReactSelect
                  isClearable
                  isSearchable
                  isLoading={referencesLoading}
                  options={classOptions}
                  value={classOptions.find((option) => option.value === item.classRef?.value) || null}
                  // eslint-disable-next-line no-undef
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  onChange={(option) =>
                    updateLineItem(index, 'classRef', option ? { value: option.value, name: option.label } : null)
                  }
                />
              </div>
              <TextField
                label="Extracted Tax Amount"
                size="small"
                type="number"
                value={item.tax?.amount ?? ''}
                onChange={(event) => updateLineItem(index, 'taxAmount', event.target.value)}
              />
              <div className="tw-flex tw-items-center tw-gap-1">
                <TextField
                  label="Line Amount"
                  size="small"
                  type="number"
                  value={item.amount?.amount ?? ''}
                  onChange={(event) => updateLineItem(index, 'amountAmount', event.target.value)}
                  error={
                    !isLineAmountValid(item.quantity, item.unitPrice?.amount, item.amount?.amount) ||
                    !invoiceTotalValidation.valid
                  }
                  helperText={
                    isLineAmountValid(item.quantity, item.unitPrice?.amount, item.amount?.amount)
                      ? ' '
                      : 'Must equal Quantity × Product Service Rate excluding GST'
                  }
                  fullWidth
                />
                <IconButton
                  aria-label="Remove line item"
                  onClick={() =>
                    setDraft((current) => ({
                      ...current,
                      lineItems: current.lineItems.filter((_, itemIndex) => itemIndex !== index)
                    }))
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            </div>
          ))}
          {!draft.lineItems?.length && <Typography color="text.secondary">No line items were extracted.</Typography>}
        </div>
        {isManualVerdInvoice && (
          <React.Fragment>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Invoice file
            </Typography>
            <div
              role="button"
              tabIndex={0}
              aria-label="Upload invoice file"
              onClick={() => invoiceFileInputRef.current?.click()}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                invoiceFileInputRef.current?.click();
              }}
              onDragEnter={(event) => {
                event.preventDefault();
                setDraggingInvoiceFile(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = 'copy';
                setDraggingInvoiceFile(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                if (!event.currentTarget.contains(event.relatedTarget)) setDraggingInvoiceFile(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setDraggingInvoiceFile(false);
                chooseInvoiceFile(event.dataTransfer.files?.[0]);
              }}
              className={`tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded tw-border-2 tw-border-dashed tw-p-6 tw-text-center ${
                draggingInvoiceFile ? 'tw-border-blue-600 tw-bg-blue-50' : 'tw-border-gray-300 tw-bg-gray-50'
              }`}
            >
              <input
                ref={invoiceFileInputRef}
                type="file"
                accept="application/pdf,image/*,.pdf"
                hidden
                onChange={(event) => {
                  chooseInvoiceFile(event.target.files?.[0]);
                  event.target.value = '';
                }}
              />
              <CloudUploadIcon color="primary" />
              <Typography>
                {invoiceFile ? invoiceFile.name : 'Drag and drop an invoice file here, or click to browse'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PDF or image, up to 20 MB. The file is uploaded when you save the bill.
              </Typography>
            </div>
            {invoiceFileError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {invoiceFileError}
              </Typography>
            )}
          </React.Fragment>
        )}
        {displayedFile.loading || displayedFile.error ? (
          <div className="tw-mb-3 tw-flex tw-min-h-64 tw-items-center tw-justify-center tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-bg-gray-50">
            {displayedFile.loading ? (
              <CircularProgress size={28} />
            ) : (
              <Typography color="text.secondary">{displayedFile.error}</Typography>
            )}
          </div>
        ) : displayedFileIsImage ? (
          <div className="tw-mb-3">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-rounded-t tw-border tw-border-b-0 tw-border-solid tw-border-gray-200 tw-bg-white tw-p-2">
              <div>
                <Typography variant="subtitle2">Invoice image</Typography>
                <div className="tw-flex tw-items-center tw-gap-2">
                  {ocrStatus === 'loading' && <CircularProgress size={12} />}
                  <Typography variant="caption" color={ocrStatus === 'error' || ocrError ? 'error' : 'text.secondary'}>
                    {ocrStatus === 'loading'
                      ? `Making image text selectable${ocrProgress ? ` (${ocrProgress}%)` : '…'}`
                      : ocrStatus === 'ready' && ocrWords.length
                      ? 'Drag across any visible text to select and copy it.'
                      : ocrError || 'Loading image…'}
                  </Typography>
                  {(ocrStatus === 'error' || (ocrStatus === 'ready' && !ocrWords.length)) && (
                    <Button size="small" onClick={() => setOcrAttempt((current) => current + 1)}>
                      Retry
                    </Button>
                  )}
                </div>
              </div>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1">
                <Tooltip title="Zoom out">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Zoom invoice image out"
                      onClick={() => changeImageZoom(-IMAGE_ZOOM_STEP)}
                      disabled={imageZoom <= MIN_IMAGE_ZOOM}
                    >
                      <ZoomOutIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button size="small" aria-label="Fit invoice image to width" onClick={() => setImageZoom(100)}>
                  {imageZoom}%
                </Button>
                <Tooltip title="Zoom in">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Zoom invoice image in"
                      onClick={() => changeImageZoom(IMAGE_ZOOM_STEP)}
                      disabled={imageZoom >= MAX_IMAGE_ZOOM}
                    >
                      <ZoomInIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>
            <div
              aria-label="Invoice image viewer"
              className="tw-flex tw-h-[42rem] tw-min-h-64 tw-items-start tw-justify-start tw-overflow-auto tw-rounded-b tw-border tw-border-solid tw-border-gray-200 tw-bg-gray-50 tw-p-4"
              onWheel={(event) => {
                if (!event.ctrlKey && !event.metaKey) return;
                event.preventDefault();
                changeImageZoom(event.deltaY < 0 ? IMAGE_ZOOM_STEP : -IMAGE_ZOOM_STEP);
              }}
            >
              <div
                className="tw-relative tw-flex-none"
                style={{
                  width: `${imageZoom}%`,
                  maxWidth: 'none',
                  margin: imageZoom <= 100 ? '0 auto' : 0,
                  containerType: 'inline-size'
                }}
              >
                <img
                  src={displayedFile.url}
                  alt="Invoice"
                  className="tw-block tw-h-auto tw-w-full tw-select-none"
                  draggable={false}
                  onLoad={(event) =>
                    setLoadedImage({
                      url: displayedFile.url,
                      width: event.currentTarget.naturalWidth,
                      height: event.currentTarget.naturalHeight
                    })
                  }
                />
                {ocrStatus === 'ready' && ocrWords.length > 0 && loadedImage.width > 0 && loadedImage.height > 0 && (
                  <div
                    data-ocr-text-layer
                    aria-label="Selectable text detected in invoice image"
                    className="tw-absolute tw-inset-0 tw-cursor-text tw-overflow-hidden tw-select-text"
                    style={{ userSelect: 'text' }}
                  >
                    {ocrWords.map((word, index) => {
                      const width = Math.max(1, word.bbox.x1 - word.bbox.x0);
                      const height = Math.max(1, word.bbox.y1 - word.bbox.y0);
                      return (
                        <span
                          key={`${word.bbox.x0}-${word.bbox.y0}-${index}`}
                          className="tw-absolute tw-block tw-cursor-text tw-select-text tw-whitespace-pre"
                          style={{
                            left: `${(word.bbox.x0 / loadedImage.width) * 100}%`,
                            top: `${(word.bbox.y0 / loadedImage.height) * 100}%`,
                            width: `${(width / loadedImage.width) * 100}%`,
                            height: `${(height / loadedImage.height) * 100}%`,
                            color: 'transparent',
                            fontFamily: 'Arial, sans-serif',
                            fontSize: `${(height / loadedImage.width) * 100}cqw`,
                            lineHeight: 1,
                            userSelect: 'text'
                          }}
                        >
                          {word.text}
                          {word.suffix}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="tw-mb-3 tw-flex tw-min-h-64 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-bg-gray-50">
            <iframe src={displayedFile.url} title="Invoice" className="tw-h-[32rem] tw-w-full tw-border-0" />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancelChanges} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Save bill'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

InvoiceDialogContent.propTypes = {
  open: PropTypes.bool,
  row: PropTypes.object,
  customerRefs: PropTypes.array,
  classRefs: PropTypes.array,
  taxCodes: PropTypes.array,
  defaultTaxCode: PropTypes.object,
  vendorList: PropTypes.array,
  vendorLoading: PropTypes.bool,
  qboTerms: PropTypes.array,
  qboTermsLoading: PropTypes.bool,
  boxshallProducts: PropTypes.array,
  boxshallProductsLoading: PropTypes.bool,
  boxshallProductsError: PropTypes.string,
  loadBoxshallProductOptions: PropTypes.func.isRequired,
  referencesLoading: PropTypes.bool,
  taxCodeLoading: PropTypes.bool,
  onCreateBoxshallProduct: PropTypes.func.isRequired,
  onCheckDocNumbers: PropTypes.func.isRequired,
  onCancelChanges: PropTypes.func.isRequired,
  onApplyChanges: PropTypes.func.isRequired
};

export default InvoiceDialogContent;
